import React, { useState } from 'react';
import { Archive, ArrowBendUpRight, CaretDown, CheckCircle, Circle, EnvelopeOpen, MagnifyingGlass, Megaphone, Trash, Tray, X, type Icon } from '@phosphor-icons/react';
import { Popover, Sheet } from '../../../../../components/immobilien/ui';

type ObjektLite = { id: string; objektnummer: string | null; bezeichnung: string | null };

/** Sammelaktionen für die ausgewählten Mails (README §7). Texte und Aktionen wie bisher. */
export function Sammelleiste({ anzahl, busy, werbungOrdner, archivOrdner, geloeschtOrdner = false, onWiederherstellen, objekte, mobil, angeheftet = true, onErledigt, onGelesen, onWerbung, onArchivieren, onLoeschen, onVerschieben, onAufheben }: {
  anzahl: number;
  busy: boolean;
  werbungOrdner: boolean;
  archivOrdner: boolean;
  objekte: ObjektLite[];
  mobil: boolean;
  /** Oben angeheftet beim Scrollen; im Querformat aus, dort wäre sie fast halb so hoch wie der Schirm. */
  angeheftet?: boolean;
  onErledigt: (wert: boolean) => void;
  onGelesen: () => void;
  onWerbung: (wert: boolean) => void;
  onArchivieren: (wert: boolean) => void;
  onLoeschen: () => void;
  /** #544: im Ordner „Gelöscht“ — Wiederherstellen statt Archivieren, Löschen = endgültig */
  geloeschtOrdner?: boolean;
  onWiederherstellen?: () => void;
  onVerschieben: (objektId: string) => void;
  onAufheben: () => void;
}) {
  const h = mobil ? 44 : 34;
  const [suche, setSuche] = useState('');
  const [sheet, setSheet] = useState(false);
  const knopf = (label: string, I: Icon, onClick: () => void, gefahr = false) => (
    <button type="button" disabled={busy} onClick={onClick} className="immo-hover-flaeche flex items-center cursor-pointer"
      style={{ height: h, padding: '0 13px', gap: 6, borderRadius: h / 2, border: 'none', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
        background: gefahr ? 'var(--bad-soft)' : 'var(--bg)', color: gefahr ? 'var(--bad)' : 'var(--ink)', opacity: busy ? 0.5 : 1 }}>
      <I size={14} weight="bold" />{label}
    </button>
  );
  const q = suche.trim().toLowerCase();
  const verschiebenKnopf = (offen: boolean, onClick: () => void, ref?: React.Ref<HTMLButtonElement>) => (
    <button ref={ref} type="button" disabled={busy} onClick={onClick} aria-expanded={offen}
      className="immo-hover-flaeche flex items-center cursor-pointer"
      style={{ height: h, padding: '0 13px', gap: 6, borderRadius: h / 2, border: 'none', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', background: 'var(--bg)', color: 'var(--ink)', opacity: busy ? 0.5 : 1 }}>
      <ArrowBendUpRight size={14} weight="bold" />Verschieben<CaretDown size={10} weight="bold" style={{ transform: offen ? 'rotate(180deg)' : 'none' }} />
    </button>
  );
  const objektListe = (schliessen: () => void) => (
    <div className="flex flex-col" style={{ gap: 2 }}>
      <label className="flex items-center" style={{ gap: 8, height: 44, margin: mobil ? '0 0 6px' : 4, padding: '0 12px', borderRadius: 13, background: 'var(--fill2)' }}>
        <MagnifyingGlass size={14} color="var(--ink3)" aria-hidden />
        <input autoFocus={!mobil} value={suche} onChange={e => setSuche(e.target.value)} placeholder="Objekt suchen…" aria-label="Objekt suchen"
          className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: mobil ? 16 : 13, color: 'var(--ink)' }} />
      </label>
      {treffer.map(o => (
        <button key={o.id} type="button" onClick={() => { schliessen(); onVerschieben(o.id); }}
          className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
          style={{ minHeight: mobil ? 44 : 36, padding: '0 10px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 13, fontWeight: 600 }}>
          <span className="truncate">{o.bezeichnung || o.objektnummer || o.id}</span>
        </button>
      ))}
    </div>
  );
  const treffer = objekte.filter(o => !q || `${o.bezeichnung || ''} ${o.objektnummer || ''}`.toLowerCase().includes(q)).slice(0, 30);

  return (
    <div role="toolbar" aria-label="Sammelaktionen" className={`${angeheftet ? 'sticky' : ''} flex flex-wrap items-center`}
      style={{ top: 8, zIndex: 20, gap: 6, padding: 8, borderRadius: 20, background: 'var(--acc-soft)', backdropFilter: 'blur(28px) saturate(170%)', WebkitBackdropFilter: 'blur(28px) saturate(170%)', boxShadow: 'var(--glass-sh)' }}>
      <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 800, color: 'var(--acc)', padding: '0 8px' }}>{anzahl} ausgewählt</span>
      {knopf('Erledigt', CheckCircle, () => onErledigt(true))}
      {knopf('Offen', Circle, () => onErledigt(false))}
      {knopf('Gelesen', EnvelopeOpen, onGelesen)}
      {werbungOrdner ? knopf('Keine Werbung', Tray, () => onWerbung(false)) : knopf('Werbung', Megaphone, () => onWerbung(true))}
      {geloeschtOrdner
        ? knopf('Wiederherstellen', Tray, () => onWiederherstellen?.())
        : archivOrdner ? knopf('Wiederherstellen', Tray, () => onArchivieren(false)) : knopf('Archivieren', Archive, () => onArchivieren(true))}
      {knopf(geloeschtOrdner ? 'Endgültig löschen' : 'Löschen', Trash, onLoeschen, true)}
      {/* Mobil als Sheet: das Popover schließt bei resize – die Bildschirmtastatur würde es sofort zumachen. */}
      {mobil ? (
        <>
          {verschiebenKnopf(sheet, () => { setSuche(''); setSheet(true); })}
          {sheet && (
            <Sheet titel="Verschieben" untertitel={`${anzahl} ausgewählt`} onSchliessen={() => setSheet(false)} maxHoehe="80vh">
              {objektListe(() => setSheet(false))}
            </Sheet>
          )}
        </>
      ) : (
        <Popover label="In Objekt verschieben" breite={280} ausrichtung="links"
          ausloeser={({ offen, umschalten, ref }) => verschiebenKnopf(offen, () => { if (!offen) setSuche(''); umschalten(); }, ref)}>
          {schliessen => objektListe(schliessen)}
        </Popover>
      )}
      <button type="button" onClick={onAufheben} className="flex items-center cursor-pointer"
        style={{ marginLeft: 'auto', height: h, padding: '0 12px', gap: 5, borderRadius: h / 2, border: 'none', background: 'transparent', color: 'var(--ink2)', fontSize: 12, fontWeight: 800 }}>
        <X size={11} weight="bold" />Aufheben
      </button>
    </div>
  );
}
